/**
 * Reference data, defined the same way documents are.
 *
 * A master is a simpler document: a flat row, no lines, no approval chain, and
 * a soft delete instead of a cancellation. One generic service and one generic
 * screen serve all of them, so adding a master is a definition and nothing else.
 */
import type { ControlKind, FieldDef } from '../documents/types.js'

export type MasterGroup =
  | 'Items and materials'
  | 'Tools'
  | 'Parties'
  | 'Addresses and contacts'
  | 'Commercial'
  | 'People and teams'
  | 'Documents'
  | 'Drive'

export interface MasterColumnDef {
  key: string
  label: string
  type?: 'text' | 'money' | 'qty' | 'rate' | 'date' | 'boolean' | 'code'
  width?: number
  align?: 'left' | 'right'
  /**
   * A name read from another master, through a reference on this row — the
   * item list's Item Category (SET-001). `table` is a registry literal.
   */
  ref?: { readonly via: string; readonly table: string; readonly column?: string }
  /**
   * Names gathered from the records that point AT this row — an item's tool
   * categories, read off the tool units of that item (FIX-7). Rows of `table`
   * whose `via` is this row's id; each one's `ref` resolved to a name; the
   * distinct names joined. All registry literals.
   */
  gather?: {
    readonly table: string
    readonly via: string
    readonly ref: { readonly via: string; readonly table: string; readonly column?: string }
  }
}

export interface MasterDef {
  key: string
  route: string
  group: MasterGroup
  label: string
  labelPlural: string
  blurb: string
  /**
   * The tenant table this edits. It is spliced into SQL as an identifier, so
   * the registry IS the allow-list — nothing outside it can ever be reached.
   */
  table: string
  /** Columns searched by the list box and by every type-ahead. */
  searchColumns: readonly string[]
  orderBy?: string
  /**
   * Values forced on read and on write. `party` holds customers, suppliers and
   * subcontractors in one table separated by boolean flags; this is what keeps
   * the Customers screen showing only customers.
   */
  fixed?: Readonly<Record<string, boolean | string>>
  columns: readonly MasterColumnDef[]
  fields: readonly FieldDef[]
  /** Masters are retired, never deleted — documents still reference them. */
  softDelete?: boolean
  /**
   * The Code may be left empty: the server gives a new record the next code
   * of its sequence (ITM-00042), as the bulk upload does for a sheet without
   * codes (master-import-plan.ts `CodeGenerator`). HOME-014.
   */
  autoCode?: boolean
}

/**
 * A master's record form as titled cards (HOME-014): fields grouped by their
 * `section`, in the order each title first appears. A field without one joins
 * the first card, and the first card is "General Info" unless a field names
 * it otherwise — the reference's heading on every setup record (SET1-009).
 * Fields kept only for imports (`importOnly`) are left out.
 */
export const masterFormSections = (def: Pick<MasterDef, 'fields'>): Array<{ title: string; fields: FieldDef[] }> => {
  const shown = def.fields.filter((f) => !f.importOnly)
  const first = shown.find((f) => f.section)?.section ?? 'General Info'
  const out: Array<{ title: string; fields: FieldDef[] }> = []
  for (const field of shown) {
    const title = field.section ?? first
    const card = out.find((c) => c.title === title)
    if (card) card.fields.push(field)
    else out.push({ title, fields: [field] })
  }
  // A field with no section that came before the first titled one still
  // belongs in the first card, which must lead.
  out.sort((a, b) => (a.title === first ? -1 : b.title === first ? 1 : 0))
  return out
}

export const defineMaster = <const T extends MasterDef>(def: T): T => def

/** Shorthand for the flat field shapes masters use. */
export const m = {
  text: (key: string, label: string, required = false, width: 'full' | 'half' = 'half'): FieldDef =>
    ({ key, label, control: 'text', required, width }),
  area: (key: string, label: string): FieldDef => ({ key, label, control: 'textarea', width: 'full' }),
  num: (key: string, label: string, control: ControlKind = 'number', required = false): FieldDef =>
    ({ key, label, control, required, width: 'half' }),
  look: (key: string, label: string, entity: string, required = false): FieldDef =>
    ({ key, label, control: 'lookup', required, width: 'half', lookup: { entity } }),
  choice: (key: string, label: string, options: ReadonlyArray<[string, string]>, required = true): FieldDef =>
    ({ key, label, control: 'select', required, width: 'half', options: options.map(([value, l]) => ({ value, label: l })) }),
  flag: (key: string, label: string, hint?: string): FieldDef =>
    ({ key, label, control: 'checkbox', width: 'half', ...(hint ? { hint } : {}) }),
  date: (key: string, label: string): FieldDef => ({ key, label, control: 'date', width: 'half' }),
}
